<div class="wrapper">
  <div class="heading text-type-2">
    {{ data?.control_form?.label || formTemplate?.label || formTemplate?.name || 'Form' }}
  </div>

  <!-- ============ SECTION SELECTOR (gates QUESTION sections) ============ -->
  <!-- Reuses control-forms-details' "Section" multi-select (frequency control):
       only the chosen question sections' summary cards render below. Sectionless
       (general) questions and table sections are NOT gated. -->
  <app-dropdown
    *ngIf="sectionSelectorPresent && !readOnly"
    [enableDetailedOptionView]="false"
    [enableMultiple]="true"
    [enableSearch]="true"
    [options]="sectionOptions"
    [parentFormGroup]="form"
    class="mb-[25px]"
    controlName="frequency"
    [label]="sectionPickerLabel"
    placeholder="Search"
  />

  <!-- Ordered block stack -->
  <ng-container *ngFor="let block of blocks">

    <!-- ============ HEADER BLOCK ============ -->
    <!-- A header block is either the untagged leading block (no label) or a
         GROUP of header fields tagged with a ui_config.sections entry, which
         gets a section heading. Header groups are ALWAYS visible — unlike
         question sections they are not gated by the Section selector. -->
    <ng-container *ngIf="block.kind === 'header' && block.fields?.length">
      <div *ngIf="block.label" class="header-group-label">{{ block.label }}:</div>
      <div *ngIf="block.instructions" class="section-instructions">{{ block.instructions }}</div>
      <div
        class="header-section-form-fields border-[1px] border-[#E9ECF4] rounded-[4px] px-[12px] pt-[21px] mb-[20px]"
      >
        <ng-container *ngFor="let hf of block.fields; let i = index">
          <ng-container
            *ngTemplateOutlet="fieldControl; context: { field: hf, group: form, idx: i }"
          ></ng-container>
        </ng-container>
      </div>
    </ng-container>

    <!-- ============ QUESTIONS BLOCK (summary card -> QuestionsComponent dialog) ============ -->
    <!-- Named question sections are gated behind the Section selector
         (isQuestionSectionVisible); sectionless/general questions are always shown. -->
    <div
      *ngIf="block.kind === 'questions' && block.questions?.length && isQuestionSectionVisible(block)"
      class="questions-block mb-[40px]"
    >
      <div class="questions-section-label">{{ block.label || 'Questions' }}:</div>
      <div *ngIf="block.instructions" class="section-instructions">{{ block.instructions }}</div>
      <ng-container *ngIf="answeredCount(block) > 0; else questionsNotAnswered">
        <div (click)="openQuestionDetails(block)" class="questions-summary flex flex-col justify-center">
          <div class="w-full justify-start">
            {{ answeredCount(block) }}/{{ questionTotal(block) }} answered
          </div>
        </div>
      </ng-container>
      <ng-template #questionsNotAnswered>
        <div
          (click)="openQuestionDetails(block)"
          class="questions-summary flex flex-row flex-wrap justify-start items-center"
        >
          <mat-icon class="question-icon mr-[8px]" svgIcon="form" />
          {{ questionTotal(block) }} {{ questionTotal(block) === 1 ? 'question' : 'questions' }}
        </div>
      </ng-template>
    </div>

    <!-- ============ TABLE BLOCK ============ -->
    <!-- Mirrors the testing-from / control-forms repeating-line accordion: a
         separate "Add entry" row above the rows, then a line-accordion per row
         with a more_horiz "Delete entry" menu (configured add/delete labels). -->
    <ng-container *ngIf="block.kind === 'table' && block.columns?.length">
      <div *ngIf="block.instructions" class="section-instructions">{{ block.instructions }}</div>
      <!--  Add Entry-->
      <div
        (click)="addTableRow(block)"
        *ngIf="!readOnly"
        class="flex flex-row justify-between items-center mb-[20px]"
      >
        <div class="section-heading text-type-8 !text-[#60698b]"></div>
        <div class="add-station-block flex flex-row justify-start items-center cursor-pointer w-fit">
          <mat-icon class="icon mr-[5px]" svgIcon="blue-plus" />
          <span class="text-type-9">{{ formTemplate?.ui_config?.add_line_label || 'Add entry' }}</span>
        </div>
      </div>
      <!-- Lines Accordion -->
      <div *ngFor="let rowKey of tableState[tableBlockKey(block)]?.keys; let last = last">
        <div
          [class.is-expanded]="tableState[tableBlockKey(block)]?.expanded[rowKey]"
          [id]="rowKey"
          class="line-accordion mb-[20px]"
        >
          <div class="line-header flex justify-between items-center">
            <div
              (click)="toggleTableRow(block, rowKey)"
              class="left flex justify-start items-center gap-[12px] grow"
            >
              <div class="details flex flex-col">
                <div class="line-accordion-heading text-type-8">
                  {{ rowHeading(block, rowKey) }}
                </div>
              </div>
            </div>
            <div class="right flex justify-center items-center gap-[10px]">
              <div class="chevron">
                <mat-icon
                  (click)="toggleTableRow(block, rowKey)"
                  [svgIcon]="tableState[tableBlockKey(block)]?.expanded[rowKey] ? 'chevron-up' : 'chevron-down'"
                  class="chevron-icon"
                />
              </div>
              <div class="menu-section" *ngIf="!readOnly">
                <mat-icon [matMenuTriggerFor]="rowMenu" class="menu-icon">more_horiz</mat-icon>
                <mat-menu #rowMenu="matMenu">
                  <button (click)="deleteTableRow(block, rowKey)" mat-menu-item>
                    <mat-icon svgIcon="trash" />
                    {{ formTemplate?.ui_config?.delete_line_label || 'Delete entry' }}
                  </button>
                </mat-menu>
              </div>
            </div>
          </div>
          <div class="line-content">
            <ng-container>
              <div class="fields-row input-block grid grid-cols-2 gap-x-[20px] gap-y-[20px]">
                <ng-container *ngFor="let col of block.columns; let ci = index">
                  <ng-container
                    *ngTemplateOutlet="fieldControl; context: { field: col, group: rowGroup(rowKey), idx: ci }"
                  ></ng-container>
                </ng-container>
              </div>
            </ng-container>
          </div>
        </div>
        <hr *ngIf="!last" class="hr mb-[20px]" />
      </div>
    </ng-container>

    <!-- ============ FOOTER BLOCK ============ -->
    <!-- Form-level fields rendered below the stack (e.g. the Confined Space
         permit's "other gases" note). Same field renderer + box as the untagged
         header block; never grouped, so never a heading. -->
    <div
      *ngIf="block.kind === 'footer' && block.fields?.length"
      class="footer-section-form-fields border-[1px] border-[#E9ECF4] rounded-[4px] px-[12px] pt-[21px] mb-[20px]"
    >
      <ng-container *ngFor="let ff of block.fields; let i = index">
        <ng-container
          *ngTemplateOutlet="fieldControl; context: { field: ff, group: form, idx: i }"
        ></ng-container>
      </ng-container>
    </div>

    <!-- ============ SIGNATURES BLOCK ============ -->
    <div *ngIf="block.kind === 'signatures'" class="signature-section mb-[40px]">
      <div class="signature-section-label">Signature:</div>
      <div *ngIf="signatureViews?.length" class="signatures flex flex-col justify-start">
        <div *ngFor="let sv of signatureViews" class="eachSignature">
          <div (click)="openSignatureDetails(sv.sourceIndex)" class="signature-content">
            <div class="role">{{ sv.role || 'Signature' }}</div>
            <ng-container *ngIf="sv.method === 'drawn' && sv.imageUrl; else nonDrawn">
              <img [src]="sv.imageUrl" [alt]="(sv.role || 'Signature') + ' signature'" class="signature-image" />
            </ng-container>
            <ng-template #nonDrawn>
              <div
                *ngIf="sv.method === 'typed_font' && sv.text; else plainSignature"
                [style.font-family]="sv.fontFamily"
                class="signature-name signature-name--font"
              >
                {{ sv.text }}
              </div>
              <ng-template #plainSignature>
                <div class="signature-name">{{ sv.text || 'Signed' }}</div>
              </ng-template>
            </ng-template>
            <div *ngIf="sv.signedAt" class="signature-signed-at">Signed {{ sv.signedAt }}</div>
          </div>
          <mat-icon
            *ngIf="!readOnly"
            (click)="removeSignature(sv.sourceIndex)"
            class="signature-remove"
            svgIcon="trash"
          />
        </div>
      </div>
      <div
        *ngIf="!readOnly"
        (click)="openSignatureDetails(null)"
        class="signature flex flex-row flex-wrap justify-start items-center"
      >
        <mat-icon class="add-icon mr-[8px]" svgIcon="plus" />
        Add Signature
      </div>
    </div>
  </ng-container>

  <!-- ============ STATION RANGE / LOCATION ============ -->
  <!-- Rendered AFTER the block stack (not a block — mirrors control-forms-details,
       which renders location/station below the form fields). Shown when the form or
       project opts into station ranges (displayStationRange). -->
  <div *ngIf="displayStationRange" class="location-station-section mb-[40px]">
    <div class="location-station-header flex justify-between items-center mb-[12px]">
      <div class="location-station-label">Location station:</div>
      <mat-icon *ngIf="!readOnly" [matMenuTriggerFor]="locationStationMenu" class="location-station-menu-icon"
        >more_horiz
      </mat-icon>
      <mat-menu #locationStationMenu="matMenu">
        <button (click)="openLocationPicker()" mat-menu-item>
          <mat-icon svgIcon="marker-outline" />
          Add location
        </button>
        <button *ngIf="hasPublishedSheets" (click)="markOnDrawing()" mat-menu-item data-testid="location-menu-mark-on-drawing">
          <mat-icon svgIcon="markup-line" />
          Mark on drawing
        </button>
        <button (click)="openStationRangeDetails()" mat-menu-item>
          <mat-icon svgIcon="plus" />
          Add station
        </button>
        <button *ngIf="showLocationOrStationSection()" (click)="deleteLocation()" mat-menu-item>
          <mat-icon svgIcon="trash" />
          Clear
        </button>
      </mat-menu>
    </div>

    <app-location-station-card
      *ngIf="showLocationOrStationSection(); else addStationRange"
      (cardClicked)="openLocationOrStationDetails()"
      (onDelete)="deleteLocation()"
      [form]="form"
      [latitude]="form?.controls?.latitude?.value"
      [longitude]="form?.controls?.longitude?.value"
      [stationRange]="stationRangeDisplayValue"
      [hideLabel]="true"
      [isEditable]="!readOnly"
      [useExternalClickHandler]="true"
      [recordLabel]="'form'"
    />
    <ng-template #addStationRange>
      <div
        *ngIf="!readOnly"
        [matMenuTriggerFor]="locationStationMenu"
        class="station-range flex flex-row flex-wrap justify-start items-center cursor-pointer"
        data-testid="generic-form-add-location-station-range"
      >
        <mat-icon class="add-icon mr-[8px]" svgIcon="plus" />
        Add location or station
      </div>
    </ng-template>
  </div>

  <!-- ============ COMMENT (revealed by the action bar's "Add comment") ============ -->
  <div *ngIf="displayCommentSection" class="comment-section mb-[40px]">
    <app-text-area [parentFormGroup]="form" controlName="comment" label="COMMENT" />
  </div>

  <!-- ============ MEDIA PREVIEW (action-bar photos/videos) ============ -->
  <app-pictures-section
    (onDelete)="deletePicture($event)"
    *ngIf="mediaFiles?.length"
    [pictures]="mediaFiles"
    [isEditable]="!readOnly"
    class="pictures-section mb-[40px]"
  />

  <!-- ============ PIN LOCATION (when station range not enabled) ============ -->
  <app-location-station-card
    (cardClicked)="openLocationOrStationDetails()"
    (onDelete)="deleteLocation()"
    *ngIf="!displayStationRange && showLocationSection()"
    [form]="form"
    [isEditable]="!readOnly"
    [latitude]="form?.controls?.latitude?.value"
    [longitude]="form?.controls?.longitude?.value"
    [useExternalClickHandler]="false"
    [recordLabel]="'form'"
    class="location-section mb-[40px]"
  />
</div>

<!-- ============ SHARED FIELD CONTROL RENDERER ============ -->
<!-- A field with `ui_config.instructions` renders its control plus helper text in
     a single wrapper, so the pair stays one cell of the 2-col table-row grid (the
     wrapper takes col-span-2 for full-width controls). Fields WITHOUT
     instructions render exactly as before — bare, no wrapper. -->
<ng-template #fieldControl let-field="field" let-group="group" let-idx="idx">
  <ng-container *ngIf="field?.ui_config?.instructions; else controlOnly">
    <div class="field-with-instructions" [class.col-span-2]="isFullWidthField(field)">
      <ng-container
        *ngTemplateOutlet="fieldSwitch; context: { field: field, group: group, idx: idx }"
      ></ng-container>
      <div class="field-instructions">{{ field?.ui_config?.instructions }}</div>
    </div>
  </ng-container>
  <ng-template #controlOnly>
    <ng-container
      *ngTemplateOutlet="fieldSwitch; context: { field: field, group: group, idx: idx }"
    ></ng-container>
  </ng-template>
</ng-template>

<!-- Mirrors the control-forms field renderer; switches on componentType (falls
     back to type) so dropdown/string/number/date/time/textarea/boolean render. -->
<ng-template #fieldSwitch let-field="field" let-group="group" let-idx="idx">
  <ng-container [ngSwitch]="field?.componentType || field?.type">
    <!-- Text Input (string) — display_only falls back to a read-only view field,
         matching control-forms-details. -->
    <ng-container *ngSwitchCase="'string'">
      <ng-container *ngIf="!field?.display_only; else displayOnly">
        <app-text-input
          [controlName]="field?.name"
          [id]="field?.name + '_' + idx"
          [label]="field?.label + (field?.uom ? ' (' + field?.uom + ')' : '')"
          [parentFormGroup]="group"
          class="mb-[20px]"
        />
      </ng-container>
      <ng-template #displayOnly>
        <app-view-field
          [id]="field?.name + '_' + idx"
          [label]="field?.label + (field?.uom ? ' (' + field?.uom + ')' : '')"
          [value]="group?.controls[field?.name]?.value"
          class="mb-[40px]"
        />
      </ng-template>
    </ng-container>
    <!-- Number Type — display_only falls back to a read-only view field. -->
    <ng-container *ngSwitchCase="'number'">
      <ng-container *ngIf="!field?.display_only; else displayOnly">
        <app-number-input
          [controlName]="field?.name"
          [id]="field?.name + '_' + idx"
          [isDecimalAllowed]="true"
          [label]="field?.label + (field?.uom ? ' (' + field?.uom + ')' : '')"
          [parentFormGroup]="group"
          class="mb-[20px]"
        />
      </ng-container>
      <ng-template #displayOnly>
        <app-view-field
          [id]="field?.name + '_' + idx"
          [label]="field?.label + (field?.uom ? ' (' + field?.uom + ')' : '')"
          [value]="group?.controls[field?.name]?.value"
          class="mb-[40px]"
        />
      </ng-template>
    </ng-container>
    <!-- Dropdown Type — mirrors control-forms header dropdown (selectionString,
         showCount, and the allow_other companion text input). -->
    <ng-container *ngSwitchCase="'dropdown'">
      <app-dropdown
        [controlName]="field?.name"
        [enableMultiple]="field?.multi_select"
        [enableOthers]="!!field?.allow_other"
        [enableSearch]="true"
        [id]="field?.name + '_' + idx"
        [label]="field?.label + (field?.uom ? ' (' + field?.uom + ')' : '')"
        [options]="field?.ui_config?.pick_list_values?.length ? field?.ui_config?.pick_list_values : picklistOptions[field?.name]"
        [parentFormGroup]="group"
        [selectionString]="group?.controls[field?.name]?.value?.length > 1 ? field?.label + 's' : field?.label"
        [showCount]="field?.name === 'activity_id' || field?.name === '_bid_item'"
        class="mb-[20px]"
      />
      <app-text-input
        *ngIf="group?.controls[field?.name + '_others']"
        [controlName]="field?.name + '_others'"
        [id]="field?.name + '_others' + '_' + idx"
        [label]="field?.label + ' ' + 'others'"
        [parentFormGroup]="group"
        class="mb-[20px]"
      />
    </ng-container>
    <app-date-picker
      *ngSwitchCase="'date'"
      [controlName]="field?.name"
      [id]="field?.name + '_' + idx"
      [label]="field?.label + (field?.uom ? ' (' + field?.uom + ')' : '')"
      [parentFormGroup]="group"
      class="mb-[20px]"
    />
    <app-time-select
      *ngSwitchCase="'time'"
      [amPmControlName]="field?.name + '_am_pm'"
      [controlName]="field?.name"
      [id]="field?.name + '_' + idx"
      [label]="field?.label + (field?.uom ? ' (' + field?.uom + ')' : '')"
      [parentFormGroup]="group"
      [timePeriodControlName]="field?.name + '_timePeriod'"
      class="mb-[20px]"
    />
    <app-text-area
      *ngSwitchCase="'textarea'"
      [controlName]="field?.name"
      [id]="field?.name + '_' + idx"
      [label]="field?.label + (field?.uom ? ' (' + field?.uom + ')' : '')"
      [parentFormGroup]="group"
      class="col-span-2 mb-[20px]"
    />
    <div
      *ngSwitchCase="'boolean'"
      [id]="field?.name + '_' + idx"
      class="flex flex-row justify-between items-center gap-[20px] w-full col-span-2 mb-[30px]"
    >
      <div class="boolean-label text-type-13">
        {{ field?.label + (field?.uom ? ' (' + field?.uom + ')' : '') }}
      </div>
      <mat-slide-toggle
        (change)="onToggle($event, field?.name, group)"
        [checked]="group?.controls?.[field?.name]?.value"
        [disableRipple]="false"
        class="slide-toggle-btn"
        color="primary"
      />
    </div>
  </ng-container>
</ng-template>
